import React from 'react';
import constants from 'vets-json-schema/dist/constants.json';

import { makeSchemaForAllDisabilities } from '../../../../all-claims/utils/schemas';
import { formatDateRange } from '../../../../shared/utils/dates/formatting';
import {
  PROVIDER_FACILITIES_ARRAY_PATH,
  PROVIDER_TYPE_LABELS,
  USA,
} from '../../../constants';

export const STATE_LABELS = constants.formProfileStates.reduce(
  (labels, { value, label }) => ({ ...labels, [value]: label }),
  {},
);

/**
 * Returns the provider item for item-level `hideIf`/`required` callbacks.
 *
 * These callbacks also run during form-wide schema recalculation, where
 * `formData` is the full form data rather than the item, so the item is
 * looked up by index in either `formData` or `fullData`.
 *
 * @param {object} formData
 * @param {number} index - item index within `providerFacility`
 * @param {object} [fullData]
 * @returns {object} the item, or an empty object if none exists
 */
export const getProviderFacilityItem = (formData, index, fullData) =>
  (formData?.[PROVIDER_FACILITIES_ARRAY_PATH] ??
    fullData?.[PROVIDER_FACILITIES_ARRAY_PATH])?.[index] || {};

/**
 * Labels of the conditions selected for a provider, in the order they appear
 * on the conditions page. Keys for conditions no longer claimed are ignored.
 */
export const getTreatedConditionNames = (item, fullData) => {
  const selected = item?.treatedDisabilityNames || {};
  const { properties = {} } = makeSchemaForAllDisabilities(fullData || {});
  return Object.keys(properties)
    .filter(key => selected[key] === true)
    .map(key => properties[key].title);
};

export const getLocationText = item => {
  const { street, city, state, postalCode, country } =
    item?.providerFacilityAddress || {};
  return [
    street,
    city,
    country === USA ? STATE_LABELS[state] || state : '',
    postalCode,
    country,
  ]
    .filter(Boolean)
    .join(', ');
};

// "I'm not sure" is omitted from the card because it doesn't describe the
// provider; "Other" shows the Veteran's description when provided.
export const getProviderTypeText = item => {
  const { providerType, providerTypeOther } = item || {};
  if (!providerType || providerType === 'notSure') return '';
  if (providerType === 'other') {
    return providerTypeOther?.trim() || PROVIDER_TYPE_LABELS.other;
  }
  return PROVIDER_TYPE_LABELS[providerType] || '';
};

export const getTreatmentDetailsText = (item, fullData) =>
  [
    getTreatedConditionNames(item, fullData).join(', '),
    item?.treatmentDateRange?.from || item?.treatmentDateRange?.to
      ? formatDateRange(item.treatmentDateRange)
      : '',
  ]
    .filter(Boolean)
    .join('; ');

export const ProviderFacilityCardDescription = (item, _index, fullData) => {
  const lines = [
    getProviderTypeText(item),
    getLocationText(item),
    getTreatmentDetailsText(item, fullData),
  ].filter(Boolean);

  return (
    <>
      {lines.map(line => (
        <p key={line} className="vads-u-margin-y--1">
          {line}
        </p>
      ))}
    </>
  );
};

export const isProviderFacilityIncomplete = (item, fullData) => {
  const address = item?.providerFacilityAddress || {};
  return (
    !item?.providerFacilityName ||
    !address.country ||
    !address.city ||
    !getTreatedConditionNames(item, fullData).length ||
    !item?.treatmentDateRange?.from ||
    !item?.treatmentDateRange?.to
  );
};

export const privateMedicalRecordsOptions = {
  arrayPath: PROVIDER_FACILITIES_ARRAY_PATH,
  nounSingular: 'provider',
  nounPlural: 'private providers or hospitals',
  // Optional multi-page pattern (VADS "Multiple responses"): the access page
  // already captures whether the Veteran wants VA to request these records, so
  // the loop itself does not force at least one item.
  required: false,
  isItemIncomplete: isProviderFacilityIncomplete,
  text: {
    getItemName: item => item?.providerFacilityName,
    cardDescription: ProviderFacilityCardDescription,
    summaryTitle: 'Review details about other healthcare providers',
    // The delete modal's wording is set out in full rather than left to the
    // platform defaults, which derive it from `nounSingular`/`nounPlural` —
    // renaming those for a summary heading would otherwise reword the modal.
    deleteTitle: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return itemName
        ? `Delete ${itemName}’s information?`
        : 'Delete this provider’s information?';
    },
    deleteDescription: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return itemName
        ? `We’ll delete ${itemName}’s information from your list of other healthcare providers.`
        : `We’ll delete this provider’s information from your list of other healthcare providers.`;
    },
    deleteYes: () => 'Yes, delete this provider',
    deleteNo: () => 'No, keep this provider',
    alertItemDeleted: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return itemName
        ? `We’ve deleted ${itemName}’s information.`
        : `We’ve deleted this provider’s information.`;
    },
  },
};
